import { ServiceConfig } from '../services/types';
import {
  ENGRAVING_CHARACTER_COUNT_LABEL,
  ensureMinimumEngravingCharacterCount,
  getEngravingConstraints,
} from './requiredInfo';

interface SelectionLike {
  quantity?: number;
  requiredInfoValues?: Record<string, string>;
}

interface ServicePricing {
  unitCost: number;
  effectiveQuantity: number;
  totalPrice: number;
  isEngraving: boolean;
  priceSourceId: string;
}

const toNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseInt(value.trim(), 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

export const isEngravingService = (config?: ServiceConfig | null): boolean =>
  Boolean(
    config?.requiredInfo?.some((field) => field.label === ENGRAVING_CHARACTER_COUNT_LABEL),
  );

export const resolveEngravingQuantity = (selection: SelectionLike, config: ServiceConfig): number => {
  const constraints = getEngravingConstraints(config.requiredInfo);
  const rawFromValues = selection.requiredInfoValues?.[ENGRAVING_CHARACTER_COUNT_LABEL];
  if (rawFromValues != null) {
    const normalized = ensureMinimumEngravingCharacterCount(rawFromValues, constraints);
    const parsed = Number.parseInt(normalized, 10);
    if (Number.isFinite(parsed)) {
      return Math.max(constraints.min, parsed);
    }
  }

  const quantityNumeric = toNumeric(selection.quantity);
  if (quantityNumeric != null) {
    return Math.max(constraints.min, Math.trunc(quantityNumeric));
  }

  return constraints.min;
};

export const getEffectiveQuantityForService = (selection: SelectionLike, config: ServiceConfig): number => {
  if (!isEngravingService(config)) {
    const quantityNumeric = toNumeric(selection.quantity);
    return Math.max(1, quantityNumeric != null ? Math.trunc(quantityNumeric) : 1);
  }
  return resolveEngravingQuantity(selection, config);
};

const formatServicePriceSourceId = (totalPrice: number): string => {
  const rounded = Number(totalPrice.toFixed(2));
  return `SERVICE_${rounded}`;
};

export const getServicePricing = (
  selection: SelectionLike,
  config: ServiceConfig,
): ServicePricing => {
  const unitCost = Number(config.cost ?? 0);
  const effectiveQuantity = getEffectiveQuantityForService(selection, config);
  const totalPrice = unitCost * effectiveQuantity;
  const isEngraving = isEngravingService(config);
  const priceSourceId = isEngraving ? formatServicePriceSourceId(totalPrice) : 'default';

  return {
    unitCost,
    effectiveQuantity,
    totalPrice,
    isEngraving,
    priceSourceId,
  };
};

export type { SelectionLike, ServicePricing };
